import React from 'react';
import _ from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, type FieldValues, type UseFormReturn } from 'react-hook-form';

import type { AxiosError } from 'axios';
import { updateOrgProfile } from '../../../../accounts/AccountService';
import { useCowbellTranslations } from '../../../../i18n/translations';
import { getLanguageForCountry } from '../../../../i18n/country';
import type { Languages } from '../../../../i18n/i18n.language-config';
import { manageAPIError } from '../../../../utils';
import { usePlatformLanguage } from '../../../../utils/hooks/usePlatformLanguage';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import { countMissingRequired } from './countMissingRequired';
import { useSettledDirty } from './useSettledDirty';
import { usesSicClassification } from './org-industry-search';
import { useAccountDetailsSchema } from './useAccountDetailsSchema';

export interface IndustrySelection {
  readonly value: string;
  readonly meta?: Record<string, unknown>;
}

export interface OrgDetailsFormApi {
  readonly methods: UseFormReturn<FieldValues>;
  /** Whether the user has changed anything. See `useSettledDirty`. */
  readonly isDirty: boolean;
  /** Resolves true only when the profile was actually written. */
  readonly submit: () => Promise<boolean>;
  readonly reset: () => void;
  readonly missingCount: number;
  readonly defaultIndustryCode: string;
  readonly onIndustrySelect: (value: IndustrySelection) => void;
  /**
   * Which taxonomy the industry field searches, decided here rather than by
   * the panel so the codes written to the form cannot disagree with the codes
   * offered to pick from.
   */
  readonly industryLanguage: Languages;
}

/**
 * The industry autocomplete's display value.
 *
 * <p>Composing this unconditionally rendered " [undefined]" in the field for
 * any account with no industry on file yet.
 */
const industryCodeLabel = (code: unknown, secondary: unknown): string => {
  if (!code) {
    return '';
  }

  if (!secondary) {
    return `${code}`;
  }

  return `${code} [${secondary}]`;
};

/**
 * The account's secondary domains arrive as objects but edit as one
 * comma-separated field.
 */
const joinDomains = (domains: unknown): string => {
  if (Array.isArray(domains)) {
    return domains.map((entry) => entry.domain).join(',');
  }

  return (domains as string) ?? '';
};

/**
 * The Organization Details form: its default values, its schema and its submit,
 * lifted out of the old `OrgDetails` component so the page can own a single
 * save bar while this logic stays exactly as it was.
 *
 * <p>Nothing here is feature-flag gated. The old component derived an
 * `isDisabled` from `submissions_view` and rendered the whole form read-only,
 * which is FIR-21111.
 */
export const useOrgDetailsForm = ({
  accountId,
  orgData,
}: {
  readonly accountId: string;
  readonly orgData: Record<string, any>;
}): OrgDetailsFormApi => {
  const notifications = useNotifications();
  const { t } = useCowbellTranslations();
  const platformLanguage = usePlatformLanguage();

  /**
   * The account's own country decides its taxonomy, not the deployment's
   * region. `industryCode` was derived from `country` by
   * `serializeAccountIndustryCode`, so anything reading it has to branch the
   * same way or the two quietly disagree -- the platform flags are a fallback
   * for an account with no country on file, nothing more.
   */
  const industryLanguage =
    getLanguageForCountry(orgData.country) ?? platformLanguage;
  const usesSic = usesSicClassification(industryLanguage);
  const schema = useAccountDetailsSchema(usesSic);

  const defaultValues = React.useMemo(
    () => ({
      isFranchise: orgData.isFranchise
        ? { value: true, label: 'Yes' }
        : { value: false, label: 'No' },
      ownershipType: orgData.ownershipType,
      revenue: `${orgData.revenue || ''}`,
      domains: joinDomains(orgData.domains),
      yearEstablished: `${orgData.yearEstablished || ''}`,
      noOfEmployeesAll: `${orgData.noOfEmployeesAll || ''}`,
      dunsNumber: `${orgData.dunsNumber || ''}`,
      // The account's own NAICS code, never the derived `industryCode`: on a
      // SIC market that field holds the *SIC* code, and posting it back as
      // `naicsCode` is the 400 in OMNI-999. These defaults have to produce the
      // same payload `onIndustrySelect` does; they used to disagree, so a save
      // failed unless the user happened to re-pick their industry first.
      naicsCode: `${orgData.naicsCode || ''}`,
      ...(usesSic && {
        sicCode: `${orgData.sicCode || ''}`,
        sicPrimaryDesc: orgData.sicPrimaryDesc ?? '',
        sicSecondaryDesc: orgData.sicSecondaryDesc ?? '',
      }),
      ..._.pick(orgData, [
        'name',
        'secondaryIndustry',
        'domainName',
        'dbaOrTradestyle',
      ]),
    }),
    [orgData, usesSic]
  );

  const methods = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const onSubmit = async ({
    isFranchise,
    ...formData
  }: FieldValues): Promise<boolean> => {
    const payload: Record<string, unknown> = {
      isFranchise: isFranchise?.value,
      ...formData,
      accountId,
    };
    delete payload.secondaryIndustry;

    try {
      await updateOrgProfile({}, payload);
    } catch (error) {
      // The server's own message, as the address form already does. A blanket
      // "Something went wrong" hid `[naicsCode: Naics Code must be 6 digits]`
      // from the policyholder and from the ticket that reported it.
      notifications.error(manageAPIError(error as AxiosError));
      // Left dirty on purpose -- see the note in useOrgAddressForm. Resetting
      // after a rejected save clears the save bar and the edit reads as saved.
      return false;
    }

    notifications.success(`${t('organization')} Profile Updated Successfully`);
    methods.reset({ isFranchise, ...formData });
    rebaseBaseline();
    return true;
  };

  /**
   * Writes the picked industry across its sibling fields.
   *
   * <p>Mirrors what the composition this replaces did -- the old panel's own
   * handler plus `LanguageIndustryAutoComplete`'s. The SIC branch is not a
   * variation on the NAICS one: the code searched is the SIC code, so the
   * NAICS code has to come off `meta` instead of the picked value, and the
   * secondary industry takes the SIC description rather than the NAICS one.
   * Flattening the two branches wrote the SIC code into `naicsCode` and left
   * the wrong description behind it.
   */
  const onIndustrySelect = (value: IndustrySelection) => {
    const dirty = { shouldDirty: true } as const;
    const meta = (value.meta ?? {}) as Record<string, unknown>;

    methods.setValue('industryClass', meta.industryClass, dirty);

    if (usesSic) {
      methods.setValue('sicCode', meta.sicCode, dirty);
      methods.setValue('sicPrimaryDesc', meta.sicPrimaryDesc, dirty);
      methods.setValue('sicSecondaryDesc', meta.sicSecondaryDesc, dirty);
      methods.setValue('secondaryIndustry', meta.sicSecondaryDesc, dirty);
      methods.setValue('naicsCode', meta.naicsCode, dirty);
      return;
    }

    methods.setValue('secondaryIndustry', meta.secondaryIndustry, dirty);
    methods.setValue('naicsCode', value.value, dirty);
  };

  const values = methods.watch();
  const { isDirty, rebaseBaseline } = useSettledDirty(
    values,
    methods.getValues
  );

  /**
   * Reports whether the save happened.
   *
   * <p>`handleSubmit` resolves either way -- it simply does not call the
   * handler when the resolver rejects -- so the page cannot tell a blocked or
   * failed save from a successful one without this. It was showing the header
   * "Saved" flag for both.
   */
  const submit = async (): Promise<boolean> => {
    let saved = false;
    await methods.handleSubmit(async (values) => {
      saved = await onSubmit(values);
    })();
    return saved;
  };

  return {
    methods,
    isDirty,
    submit,
    reset: () => {
      methods.reset(defaultValues);
      rebaseBaseline();
    },
    missingCount: countMissingRequired(
      schema.describe().fields as Record<string, unknown>,
      values
    ),
    // The code this market classifies by, which on a SIC market is not the one
    // held in `naicsCode`. Reading `naicsCode` unconditionally showed an AU or
    // UK account its NAICS code the moment it picked an industry, having shown
    // it the SIC code up to that point.
    defaultIndustryCode: industryCodeLabel(
      usesSic ? values.sicCode : values.naicsCode,
      values.secondaryIndustry
    ),
    onIndustrySelect,
    industryLanguage,
  };
};

export default useOrgDetailsForm;
